import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { get } from 'lodash'
import { GET, POST, POST_IMAGE, PUT, DELETE } from '../service/base-api-service';
import to from '../utils/to';
import { DEFAULT_PAGE_SIZE } from '../utils/constant';
import { getIsAdminApi, getUser } from './auth.store';

const initialState = {
  userList: [],
  isLoading: false,
  tableInfo: {
    company: '',
    totalPages: 0,
    currentPage: -1,
    sortColumn: 'id',
    isAscending: true,
    searchKey: ''
  },
  editingUser: null,
  editingUserLoading: false,
  isLoadingError: false,
  eventUserList: [],
  eventUserPersonList: [],

  roles: [],
  roleActions: [],
  rolesLoading: false,
  roleActionsLoading: false,
  rolesError: null,
  roleActionsError: null,
  editingRole: null,
  editingRoleLoading: false,
  editingRoleError: null,
};

export const fetchEventUserList = createAsyncThunk('events/userlist/get', ({ viewType }) => GET(`v1/admin/${viewType.toLowerCase()}/events/users`));
export const fetchEventUserPersonList = createAsyncThunk('events/userpersonlist/get', ({ viewType }) => GET(`v1/admin/${viewType.toLowerCase()}/events/users?persons=true`));

export const getPaginatedUserList = createAsyncThunk(
  'userList/get',
  async ({ page, sortColumn = 'id', isAscending = false, company, searchKey }, { dispatch, getState }) => {
    dispatch(setTableInfo({ currentPage: page, sortColumn, isAscending, company, searchKey }));

    let url = 'v1/admin/users';
    let params = { page, sortColumn, isAscending, size: DEFAULT_PAGE_SIZE, searchKey }
    if (getIsAdminApi(getState())) {
      url = 'v1/admin/users';
      if(company){
        params = {...params, tenantId: company}
      }
    }

    return GET(url, { params });
  },
);

export const getAllUsersForLocalFilter = createAsyncThunk(
  'userList/getAllForLocalFilter',
  async (_, { rejectWithValue, getState }) => {
    try {
      let url = 'v1/admin/users';
      const params = {
        page: 0,
        sortColumn: 'fullName',
        isAscending: true,
        size: 10000,
        searchKey: '',
      };

      if (getIsAdminApi(getState())) {
        url = 'v1/admin/users';
      }

      return await GET(url, { params });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);


export const fetchUserById = createAsyncThunk('user/get-by-id', async ({ id },{ dispatch }) => {
  const data = await GET('v1/admin/users/' + id);
  dispatch(UsersAction.setSelectedCompanyToTable(data.tenantIdentifier));
  return data;
});

export const deleteUserById = createAsyncThunk('user/delete-by-id', async ({ id },  { dispatch, getState }) => {
  try{
    const response = await DELETE('v1/admin/users/' + id);
    const tableInfo = getUserTableInfo(getState());
    dispatch(getPaginatedUserList({ page: 0, ...tableInfo }));
    return null;
  }catch(err){
    return err
  }
});

// export const addNewUser = createAsyncThunk(
//   'user/addNew',
//   async ({ formValue, image, isEditing }, { rejectWithValue }) => {
//     let err, data;
//     if (image) {
//       [err, data] = await to(POST_IMAGE(image));
//       if (err) {
//         return rejectWithValue({ err });
//       }
//     }
//     if (isEditing) {
//      return PUT('v1/admin/users/' + formValue.id, { ...formValue, profilePictureURL: data ? data.secure_url : null });
//     }
//     return POST('v1/admin/users/register', { ...formValue, profilePictureURL: data ? data.secure_url : null });
//   },
// );


//JB wrote the thunk below:

// ✅ Matches APIConstants.ADMIN_ROLE_API_BASE_PATH = /v1/admin/roles
const ADMIN_ROLE_BASE = 'v1/admin/roles';

export const fetchRoles = createAsyncThunk('roles/get', async (_, { rejectWithValue }) => {
  try {
    return await GET(`${ADMIN_ROLE_BASE}`);
  } catch (err) {
    console.error('fetchRoles error:', err);
    return rejectWithValue(err?.error?.message || 'Could not fetch roles');
  }
});

export const fetchRoleActions = createAsyncThunk('roleActions/get', async (_, { rejectWithValue }) => {
  try {
    return await GET(`${ADMIN_ROLE_BASE}/actions`);
  } catch (err) {
    console.error('fetchRoleActions error:', err);
    return rejectWithValue(err?.error?.message || 'Could not fetch role actions');
  }
});


// JB rewrote the thunk
export const addNewUser = createAsyncThunk(
  'user/addNew',
  async ({ formValue, image, isEditing }, { rejectWithValue }) => {
    try {
      let imageData = null;

      if (image) {
        const [err, data] = await to(POST_IMAGE(image));

        if (err) {
          const rawMessage =
            err?.data?.error?.message ||
            err?.data?.message ||
            err?.message ||
            '';

          const uploadMessage = rawMessage.includes('File size too large')
            ? 'Selected image is too large. Maximum allowed size is 10 MB.'
            : rawMessage || 'Image upload failed';

          return rejectWithValue({
            message: uploadMessage,
            details: err,
          });
        }

        imageData = data?.secure_url || null;
      }

      const payload = {
        ...formValue,
      };

      if (imageData) {
        payload.profilePictureURL = imageData;
      }

      if (payload.roleId && !payload.role) {
        payload.role = { id: payload.roleId };
      }
      delete payload.roleId;

      if (Array.isArray(payload.assignedSiteIds)) {
        payload.assignedSiteIds = payload.assignedSiteIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id));
      }

      if (isEditing) {
        const res = await PUT('v1/admin/users/' + formValue.id, payload);
        //console.log('User updated successfully:', payload);
        return res;
      } else {
        const res = await POST('v1/admin/users/register', payload);
        return res;
      }
    } catch (err) {
      console.error('API error:', err?.error || err);

      return rejectWithValue({
        message:
          err?.data?.error?.message ||
          err?.data?.message ||
          err?.error?.message ||
          err?.message ||
          'Could not add user',
        details: err,
      });
    }
  }
);


export const addNewRole = createAsyncThunk('roles/addNew', async ({ formValue }, { rejectWithValue }) => {
  try {
    return await POST(`${ADMIN_ROLE_BASE}`, {
      name: formValue.name,
      displayName: formValue.displayName,
      description: formValue.description,
      type: formValue.type,
      actions: formValue.actions, // array of action_name strings
    });
  } catch (err) {
    console.error('Error:', err);
      return rejectWithValue(err); // ✅ keep status + message
  }
});



// ✅ Get one role by id (for edit modal hydration)
export const fetchRoleById = createAsyncThunk('roles/getById', async ({ roleId }, { rejectWithValue }) => {
  try {
    return await GET(`${ADMIN_ROLE_BASE}/${roleId}`);
  } catch (err) {
    console.error('fetchRoleById error:', err);
    return rejectWithValue(err?.error?.message || 'Could not fetch role');
  }
});

// ✅ Update role (PUT /v1/admin/roles/{roleId})
export const updateRole = createAsyncThunk('roles/update', async ({ roleId, formValue }, { rejectWithValue }) => {
  try {
    return await PUT(`${ADMIN_ROLE_BASE}/${roleId}`, {
      name: formValue.name,
      displayName: formValue.displayName,
      description: formValue.description,
      type: formValue.type,
      actions: formValue.actions,
    });
  } catch (err) {
    console.error('updateRole error:', err);
    return rejectWithValue(err?.error?.message || 'Could not update role');
  }
});


export const deleteRole = createAsyncThunk(
  'roles/delete',
  async ({ roleId }, { rejectWithValue }) => {
    try {
      await DELETE(`${ADMIN_ROLE_BASE}/${roleId}`);
      return roleId;
    } catch (err) {
      console.error('Error:', err);
      return rejectWithValue(err); // ✅ keep status + message
    }
  }
);




export const updateUser = createAsyncThunk('user/update', async ({ userId, body }, { rejectWithValue }) => {
  try {
    return await PUT('v1/admin/users/' + userId, body);
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const UsersAction = {
  setPage: (page) => (dispatch, getState) => {
    const tableInfo = getUserTableInfo(getState());
    if (tableInfo.currentPage !== page) dispatch(getPaginatedUserList({ 
      page,
      sortColumn: tableInfo.sortColumn, 
      isAscending: tableInfo.isAscending, 
      company: tableInfo.company, 
      searchKey: tableInfo.searchKey
    }));
  },
  setSelectedCompany: (company) => (dispatch, getState) => {
    const previousCompany = getUserTableInfo(getState()).company;
    if (company !== previousCompany) {
      dispatch(getPaginatedUserList({ page: 0, company, isAscending: false, sortColumn: 'id', searchKey: '' }));
    }
  },
  setSelectedCompanyToTable: (company) => (dispatch, getState) => {
    const previousCompany = getUserTableInfo(getState()).company;
    if (company !== previousCompany) {
      dispatch(setTableInfo({ site: null, company}));
    }
  },
  setSortColumn: ({sortColumn, isAscending}) => (dispatch, getState) => {
    const tableInfo = getUserTableInfo(getState());
    dispatch(getPaginatedUserList({ 
      page: 0,
      ...tableInfo, 
      sortColumn, 
      isAscending
    }));
  },
  setSearch: (searchKey) => (dispatch, getState) => {
    const tableInfo = getUserTableInfo(getState());
    dispatch(getPaginatedUserList({
      page: tableInfo.currentPage, 
      sortColumn: tableInfo.sortColumn, 
      isAscending: tableInfo.isAscending, 
      company: tableInfo.company, 
      searchKey
    }))
  }
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setTableInfo: (state, { payload }) => {
      state.tableInfo = { ...state.tableInfo, ...payload };
    },
  },
  extraReducers: {
    [getPaginatedUserList.pending]: (state) => {
      state.isLoading = true;
    },
    [getPaginatedUserList.rejected]: (state) => {
      state.isLoading = false;
      state.isLoadingError = true;
    },
    [getPaginatedUserList.fulfilled]: (state, action) => {
      state.isLoading = false;
      const { results, currentPage, pageSize, totalPages } = action.payload;
      state.userList = results;
      state.tableInfo = { ...state.tableInfo, currentPage, pageSize, totalPages };
    },
    [addNewUser.pending]: (state) => {
      state.editingUserLoading = true;
    },
    [addNewUser.rejected]: (state) => {
      state.editingUserLoading = false;
    },
    [addNewUser.fulfilled]: (state) => {
      state.tableInfo.currentPage = -1;
      state.editingUserLoading = false;
    },
    [updateUser.pending]: (state) => {
      state.isLoading = true;
    },
    [updateUser.rejected]: (state) => {
      state.isLoading = false;
    },
    [updateUser.fulfilled]: (state, { payload }) => {
      state.userList = state.userList.map((user) => (user.id === payload.id ? payload : user));
      state.isLoading = false;
    },
    [fetchUserById.pending]: (state) => {
      state.editingUserLoading = true;
      state.editingUser = null;
    },
    [fetchUserById.rejected]: (state) => {
      state.editingUserLoading = false;
    },
    [fetchUserById.fulfilled]: (state, { payload }) => {
      state.editingUserLoading = false;
      state.editingUser = payload;
    },
    [fetchEventUserList.pending]: (state) => {
      state.isLoading = true;
    },
    [fetchEventUserList.rejected]: (state) => {
      state.isLoading = false;
      state.isLoadingError = true;
    },
    [fetchEventUserList.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.eventUserList =  action.payload;
    },
    [fetchEventUserPersonList.pending]: (state) => {
      state.isLoading = true;
    },
    [fetchEventUserPersonList.rejected]: (state) => {
      state.isLoading = false;
      state.isLoadingError = true;
    },
    [fetchEventUserPersonList.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.eventUserPersonList =  action.payload;
    },




    // roles
[fetchRoles.pending]: (state) => {
  state.rolesLoading = true;
  state.rolesError = null;
},
[fetchRoles.rejected]: (state, action) => {
  state.rolesLoading = false;
  state.rolesError = action.payload || 'Failed to fetch roles';
},
[fetchRoles.fulfilled]: (state, action) => {
  state.rolesLoading = false;
  state.roles = action.payload || [];
},

// role actions
[fetchRoleActions.pending]: (state) => {
  state.roleActionsLoading = true;
  state.roleActionsError = null;
},
[fetchRoleActions.rejected]: (state, action) => {
  state.roleActionsLoading = false;
  state.roleActionsError = action.payload || 'Failed to fetch role actions';
},
[fetchRoleActions.fulfilled]: (state, action) => {
  state.roleActionsLoading = false;
  state.roleActions = action.payload || [];
},

// add role
[addNewRole.pending]: (state) => {
  state.rolesLoading = true;
},
[addNewRole.rejected]: (state) => {
  state.rolesLoading = false;
},
[addNewRole.fulfilled]: (state) => {
  state.rolesLoading = false;
},


// fetchRoleById
[fetchRoleById.pending]: (state) => {
  state.editingRoleLoading = true;
  state.editingRoleError = null;
  state.editingRole = null;
},
[fetchRoleById.rejected]: (state, action) => {
  state.editingRoleLoading = false;
  state.editingRoleError = action.payload || 'Failed to fetch role';
},
[fetchRoleById.fulfilled]: (state, action) => {
  state.editingRoleLoading = false;
  state.editingRole = action.payload || null;
},

// updateRole
[updateRole.pending]: (state) => {
  state.rolesLoading = true;
},
[updateRole.rejected]: (state, action) => {
  state.rolesLoading = false;
  state.rolesError = action.payload || 'Failed to update role';
},
[updateRole.fulfilled]: (state, action) => {
  state.rolesLoading = false;

  // keep roles list in sync
  const updated = action.payload;
  if (updated?.id) {
    state.roles = (state.roles || []).map((r) => (r.id === updated.id ? updated : r));
  }
},


// delete role
[deleteRole.pending]: (state) => {
  state.rolesLoading = true;
},
[deleteRole.rejected]: (state, action) => {
  state.rolesLoading = false;
  state.rolesError = action.payload || 'Failed to delete role';
},
[deleteRole.fulfilled]: (state, action) => {
  state.rolesLoading = false;
  const deletedId = action.payload;
  state.roles = (state.roles || []).filter((r) => r.id !== deletedId);
},



// for paginated users
[getAllUsersForLocalFilter.pending]: (state) => {
      state.isLoading = true;
      state.isLoadingError = false;
    },
    [getAllUsersForLocalFilter.rejected]: (state) => {
      state.isLoading = false;
      state.isLoadingError = true;
    },
    [getAllUsersForLocalFilter.fulfilled]: (state, action) => {
      state.isLoading = false;

      const { results = [] } = action.payload || {};
      state.userList = results;

      state.tableInfo = {
        ...state.tableInfo,
        currentPage: 0,
        totalPages: Math.ceil(results.length / DEFAULT_PAGE_SIZE),
        pageSize: DEFAULT_PAGE_SIZE,
        searchKey: '',
      };
    },

//******************************************************* */

  },
});

export const { setTableInfo } = userSlice.actions;

const getUserStore = (state) => state.user;

export const getUsersList = createSelector(getUserStore, (state) => state.userList);
export const getEventUserList = createSelector(getUserStore, (state) => state.eventUserList);
export const getEventUserPersonList = createSelector(getUserStore, (state) => state.eventUserPersonList);
export const getUsersLoading = createSelector(getUserStore, (state) => state.isLoading);
export const getUserTableInfo = createSelector(getUserStore, (state) => state.tableInfo);

export const getEditingUser = createSelector(getUserStore, (store) => store.editingUser);
export const getEditingUserLoading = createSelector(getUserStore, (store) => store.editingUserLoading);

//JB added below:
export const getRoles = createSelector(getUserStore, (s) => s.roles);
export const getRolesLoading = createSelector(getUserStore, (s) => s.rolesLoading);
export const getRoleActions = createSelector(getUserStore, (s) => s.roleActions);
export const getRoleActionsLoading = createSelector(getUserStore, (s) => s.roleActionsLoading);
export const getEditingRole = createSelector(getUserStore, (s) => s.editingRole);
export const getEditingRoleLoading = createSelector(getUserStore, (s) => s.editingRoleLoading);
//*************************************************** */

export default userSlice.reducer;
